/**
 * Firmware Manifest
 * Defines available firmware versions bundled with the web app
 */

export interface FirmwareMetadata {
  version: string
  filename: string
  size: number
  sha256?: string
  buildDate: string
  chipset: string
  minFlashSize: string
  features: string[]
  changelog: string[]
  releaseNotes?: string
  critical?: boolean  // Is this a critical security/bugfix update?
}

export interface FirmwareManifest {
  latestVersion: string
  latestFirmware: string
  latestMetadata: string
  available: FirmwareMetadata[]
}

/**
 * Bundled firmware metadata
 * Loaded dynamically from generated JSON files
 */
let cachedManifest: FirmwareManifest | null = null

/**
 * Load firmware manifest from generated JSON
 */
async function loadManifest(): Promise<FirmwareManifest> {
  if (cachedManifest) {
    return cachedManifest
  }

  try {
    // Load the index.json generated by build.sh
    // Add cache-busting to ensure we get the latest version
    const response = await fetch(`/firmware/index.json?t=${Date.now()}`)
    if (!response.ok) {
      throw new Error('Failed to load firmware index')
    }

    const index = await response.json()

    // Load metadata for each available firmware
    const available: FirmwareMetadata[] = []
    for (const fw of index.available) {
      const metaResponse = await fetch(`/firmware/${fw.metadata}`)
      if (metaResponse.ok) {
        const metadata = await metaResponse.json()
        available.push({
          ...metadata,
          filename: fw.firmware,
          releaseNotes: `
## What's New in v${metadata.version}

### OTA Updates
- Wirelessly update firmware from the web app
- No USB cable required
- Progress shown via LEDs and OLED display
- Automatic rollback if update fails

### Improvements
- Better BLE connection stability
- Faster pairing algorithm
- Enhanced error handling

### Features
${metadata.features.map((f: string) => `- ${f}`).join('\n')}

### Changes
${metadata.changelog.map((c: string) => `- ${c}`).join('\n')}
          `.trim(),
          critical: false
        })
      }
    }

    cachedManifest = {
      latestVersion: index.latestVersion,
      latestFirmware: index.latestFirmware,
      latestMetadata: index.latestMetadata,
      available
    }

    return cachedManifest
  } catch (error) {
    console.error('Failed to load firmware manifest:', error)
    // Fallback to hardcoded manifest if dynamic loading fails
    return FALLBACK_MANIFEST
  }
}

/**
 * Fallback manifest in case dynamic loading fails
 */
const FALLBACK_MANIFEST: FirmwareManifest = {
  latestVersion: "1.1.0",
  latestFirmware: "m-scale-central-latest.bin",
  latestMetadata: "m-scale-central-latest.json",
  available: []
}

export const FIRMWARE_MANIFEST = {
  get: loadManifest
}

/**
 * Get latest firmware metadata
 */
export async function getLatestFirmware(): Promise<FirmwareMetadata | null> {
  const manifest = await FIRMWARE_MANIFEST.get()
  return manifest.available.find(
    fw => fw.version === manifest.latestVersion
  ) || null
}

/**
 * Get firmware by version
 */
export async function getFirmwareByVersion(version: string): Promise<FirmwareMetadata | null> {
  const manifest = await FIRMWARE_MANIFEST.get()
  return manifest.available.find(fw => fw.version === version) || null
}

/**
 * Check if a newer firmware is available
 */
export function isNewerVersion(currentVersion: string, compareVersion: string): boolean {
  // Parse version string, handling suffixes like "-rollback-test"
  const parse = (v: string) => {
    // Remove leading 'v' if present
    const cleaned = v.replace(/^v/, '')
    // Split by '.' and take only numeric parts before any '-' suffix
    const parts = cleaned.split('.').map(part => {
      const numPart = part.split('-')[0]
      return parseInt(numPart, 10) || 0
    })
    return parts
  }

  const [cMajor = 0, cMinor = 0, cPatch = 0] = parse(currentVersion)
  const [nMajor = 0, nMinor = 0, nPatch = 0] = parse(compareVersion)

  if (nMajor > cMajor) return true
  if (nMajor < cMajor) return false
  if (nMinor > cMinor) return true
  if (nMinor < cMinor) return false
  return nPatch > cPatch
}

/**
 * Get firmware file URL
 */
export function getFirmwareURL(filename: string): string {
  // Firmware files are served from /firmware/ directory
  return `/firmware/${filename}`
}
